import React, { useState, useRef, useEffect } from 'react';
import { Bot, X, Send, Minimize2 } from 'lucide-react';
import './CapoBizChatbot.css';

const CapoBizChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showPopup, setShowPopup] = useState(true);

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm CapoBiz Assistant. How can I help you today?",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

const websiteContext = `
You are CapoBiz AI Assistant, a knowledgeable, professional, and helpful chatbot
for CapoBiz.com — a cloud-based POS and retail business management system.

Your role:
- Clearly explain CapoBiz features and modules.
- Answer user questions in a natural, conversational, and practical manner.
- Guide users step-by-step when they ask "how to" questions.
- If exact internal steps or UI labels are not publicly documented, explain
  the standard and expected workflow of a POS or retail management system
  like CapoBiz without making false or unverifiable claims.

Guidelines:
- Use <b>bold</b> for emphasis.
- Use <ul><li>...</li></ul> for lists.
- Be professional, friendly, and solution-oriented.
- Prefer clear, practical explanations over marketing language.
- Use bullet points or numbered steps where helpful.
- You MAY explain logical workflows even if exact UI labels are not specified.
- Avoid guessing sensitive, private, or internal company information.
- If something is truly unknown, say so politely and suggest contacting support.

CapoBiz Overview:
- Cloud-based POS and retail business management software
- Designed for small to medium-sized businesses
- Suitable for retail stores, pharmacies, restaurants, wholesalers,
  mobile shops, and trading businesses
- Helps automate daily business operations and reporting

Key CapoBiz Modules & Features:
<ul>
  <li>Sales & Billing (POS)</li>
  <li>Inventory & Stock Management</li>
  <li>Product & Category Management</li>
  <li>Customer Management</li>
  <li>Accounts & Expense Tracking</li>
  <li>Reports & Analytics</li>
  <li>Multi-store / Multi-warehouse support</li>
  <li>Barcode scanning & POS hardware integration</li>
  <li>User roles & access control</li>
</ul>

Typical CapoBiz Workflows You May Explain:
- Creating products and managing stock
- Making sales using the POS billing system
- Managing customers and purchase history
- Tracking expenses and profit reports
- Handling multiple stores or warehouses
- Generating daily, monthly, or custom reports

Conversation Rules:
- Greet the user only at the start of a new conversation.
- If the user has already sent a message, do NOT repeat greetings.
- Do NOT start responses with "Hello" or "Hi" unless the conversation just started.
- Be concise but informative.
- If unsure about a feature or internal detail, suggest contacting CapoBiz support.

Contact & Support:
- Email: info@technicmentors.com
- WhatsApp: +92-311-1122144
- Users can request a demo or contact support directly through the website
`;


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isOpen && showPopup) {
      const timer = setTimeout(() => setShowPopup(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, showPopup]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

const callGeminiAPI = async (userQuestion, isFirstMessage = false) => {
  const API_KEY = 'AIzaSyD5ym_nDhlNr9cOkZd9dps9hb-kapNPA-k';
  const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${API_KEY}`;

  const greetingInstruction = isFirstMessage
    ? 'You may greet the user at the start of the conversation.'
    : 'Do NOT greet the user. Start answering directly.';

  const prompt = `${websiteContext}\n${greetingInstruction}\nUser Question: "${userQuestion}"`;

  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: prompt
          }]
        }]
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('API Error:', errorData);

      if (response.status === 429) return 'Limit exceeded.';
      if (response.status === 404) return 'Warning: Not Found';

      throw new Error(`API Error: ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();

    if (data.candidates && data.candidates[0] && data.candidates[0].content) {
      const textContent = data.candidates[0].content.parts
        .map(part => part.text || '')
        .join('');
      return textContent;
    }

    throw new Error('Invalid response format from API');
  } catch (error) {
    console.error('Error calling Gemini API:', error);
    return `Sorry, I encountered an error: ${error.message}. Please try again later.`;
  }
};

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const botResponse = await callGeminiAPI(inputMessage);
      
      const assistantMessage = {
        role: 'assistant',
        content: botResponse,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <div className="chatbot-button-container">
          {/* Sliding Popup */}
          {showPopup && (
            <div className="chatbot-popup">
              <p>Ask CapoBiz AI</p>
            </div>
          )}

          {/* Chat Button */}
          <button
            onClick={() => {
              setIsOpen(true);
              setShowPopup(false);
            }}
            className="chatbot-button"
            aria-label="Open chat"
          >
            <Bot size={32} />
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`chatbot-window ${isMinimized ? 'minimized' : ''}`}>
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">
                <span>🤖</span>
              </div>
              <div>
                <h3>CapoBiz Assistant</h3>
                <p>Always here to help</p>
              </div>
            </div>
            <div className="chatbot-header-actions">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="chatbot-icon-button"
                aria-label="Minimize chat"
              >
                <Minimize2 size={18} />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="chatbot-icon-button"
                aria-label="Close chat"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages Container */}
              <div className="chatbot-messages">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`message-wrapper ${message.role}`}
                  >
                    <div className={`message-bubble ${message.role}`}>
                      <p
                        className="message-text"
                        dangerouslySetInnerHTML={{
                          __html: message.content
                            .replace(/\n/g, '<br/>')
                            .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
                            .replace(/\*(.*?)\*/g, '<li>$1</li>')
                        }}                                              
                      />
                      <p className="message-time">
                        {message.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="message-wrapper assistant">
                    <div className="message-bubble assistant">
                      <div className="typing-indicator">
                        <div className="typing-dot"></div>
                        <div className="typing-dot"></div>
                        <div className="typing-dot"></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="chatbot-input-area">
                <div className="chatbot-input-wrapper">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    className="chatbot-input"
                    disabled={isLoading}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={isLoading || !inputMessage.trim()}
                    className="chatbot-send-button"
                    aria-label="Send message"
                  >
                    <Send size={20} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default CapoBizChatbot;